import { VerificationCodeForm } from "../onboarding/VerificationCodeForm";
import { useEffect, useRef, useState } from "preact/hooks";
import { useOnboardingFlow } from "../onboarding/useOnboardingFlow";

type Props = {
  validateEmail: (email: string) => string | undefined;
  onComplete: () => void;
  onCancel: () => void;
};

export function AddEmailFlow({ validateEmail, onComplete, onCancel }: Props) {
  const flow = useOnboardingFlow({ onComplete, skipInitialization: true });
  const [emailError, setEmailError] = useState<string>();
  const [email, setEmail] = useState("");
  const emailInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (flow.step === "enterEmail") emailInput.current?.focus();
  }, [flow.step]);

  if (flow.step === "verify")
    return (
      <VerificationCodeForm
        challenge={flow.challenge}
        pending={flow.pending}
        onSubmit={flow.linkEmailWithCode}
        onResend={flow.resend}
        onChangeEmail={flow.changeEmail}
        onCancel={onCancel}
        submitLabel="Verify email"
      >
        <h3>Verify email</h3>
        {flow.error && (
          <p class="payment-error" role="alert">
            {flow.error}
          </p>
        )}
        <p class="muted">If {flow.email} matches an account, we'll send a verification code.</p>
      </VerificationCodeForm>
    );

  return (
    <form
      aria-busy={flow.pending}
      onSubmit={(event) => {
        event.preventDefault();
        const error = validateEmail(email.trim());
        setEmailError(error);
        if (!error) void flow.start(email);
      }}
    >
      <h3>Add another email</h3>
      {(emailError || flow.error) && (
        <p class="payment-error" role="alert">
          {emailError || flow.error}
        </p>
      )}
      <label class="form-control">
        <span>Email address</span>
        <input
          type="email"
          autoComplete="email"
          ref={emailInput}
          required
          value={email}
          disabled={flow.pending}
          onInput={(event) => setEmail(event.currentTarget.value)}
        />
      </label>
      <div class="linked-accounts-actions">
        <button class="primary-action" type="submit" disabled={flow.pending}>
          {flow.pending ? "Sending…" : "Send code"}
        </button>
        <button class="secondary-action" type="button" disabled={flow.pending} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
